<template>
  <div id="statistics">
    <div class="container">

      <div class="columns">
        <div class="group mr-2">
          <div>&nbsp</div>
          <button class="btn btn-primary" @click="generate">{{ $t('generate') }}</button>
        </div>
        <div class="group mr-2">
          <label>{{ $t('start') }}</label>
          <datetime v-model="parameters.start" :max-datetime="maxDate" />
        </div>
        <div class="group mr-2">
          <label>{{ $t('end') }}</label>
          <datetime v-model="parameters.end" :max-datetime="maxDate" />
        </div>
        <div class="group mr-2">
          <label>{{ $t('groupBy') }}</label>
          <select v-model="parameters.groupByPeriod">
            <option value="day">{{ $t('statistics.param.day') }}</option>
            <option value="month">{{ $t('statistics.param.month') }}</option>
          </select>
        </div>
      </div>

      <hr>
      <div class="middle">
        <div class="chart">
          <div class="title">{{ $t('stats.sales') }} <div class="separator">|</div>
          </div>
          <line-chart :library="chartJsOptions" :data="lineChart.data" :colors="lineChart.colors"
            :height="lineChart.height" :suffix="lineChart.suffix" :messages="{ empty: $t('stats.messages.empty') }" />
          <div class="columns">
            <div class="column">
              <div class="group" v-for="c in companies">
                <input :id="`chk_${c.id}`" type="checkbox" :value="c.id" v-model="selectedCompanies">
                <label :for="`chk_${c.id}`">{{ c.name }}</label>
              </div>
            </div>
            <div class="column">
              <div class="group" v-show="Object.keys(companies).length > 0">
                <input id="chk_show_last" type="checkbox" v-model="showLastYear">
                <label for="chk_show_last">{{ $t('stats.showLastYear') }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
  import { Core } from '@/core/Core';
  import moment from "moment-timezone";

  export default {
    name: "StatsPageBranch",

    data() {
      return {
        maxDate: moment().toISOString(),
        parameters: {
          start: null,
          end: null,
          groupByPeriod: 'month'
        },
        lineChart: {
          height: '600px',
          data: [],
          colors: [],
          suffix: " $"
        },
        chartJsOptions: {
          legend: {
            labels: {
              filter: this.filterOutPreviousLegend
            }
          }
        },
        dateTimeFormat: 'YYYY-MM-DD',
        currentByCompany: {},
        lastYearByCompany: {},
        companies: {},
        selectedCompanies: [],
        showLastYear: true,
        colors: [
          "#e6194B", "#3cb44b", "#ffe119", "#4363d8", "#f58231",
          "#911eb4", "#42d4f4", "#f032e6", "#bfef45", "#fabebe",
          "#469990", "#e6beff", "#9A6324", "#fffac8", "#800000",
          "#aaffc3", "#808000", "#ffd8b1", "#000075", "#a9a9a9",
        ]
      }
    },

    async mounted() {
      this.parameters.start = moment().startOf('year').toISOString();
      this.parameters.end = moment().endOf('day').toISOString()
      this.$store.state.pageTitleTranslationKey = "statistics.branch_title";
      this.$store.state.pageIcon = "/icon/nav/analytics.svg";
    },

    methods: {
      generate() {
        let branch = this.$route.query.branch;
        let start = this.parameters.start
          ? moment(this.parameters.start).format(this.dateTimeFormat) : null;
        let end = this.parameters.end
          ? moment(this.parameters.end).format(this.dateTimeFormat) : null;

        let startMinusOneYear = this.parameters.start
          ? moment(this.parameters.start).subtract(1, 'year').format(this.dateTimeFormat) : null;
        let endMinusOneYear = this.parameters.end
          ? moment(this.parameters.end).subtract(1, 'year').format(this.dateTimeFormat) : null;

        let timeFrame = this.parameters.groupByPeriod;

        let options = {
          dataAsObject: true,
          fields: ['subTotal', 'cId'],
          groupBy: ['company', timeFrame],
          filters: {
            branch: branch,
            start: start,
            end: end
          }
        };
        let lastYearOptions = {
          dataAsObject: true,
          fields: ['subTotal', 'cId'],
          groupBy: ['company', timeFrame],
          filters: {
            branch: branch,
            start: startMinusOneYear,
            end: endMinusOneYear
          }
        };
        Promise
          .all([
            Core.request("POST", '/api/stats-builder/sales', options),
            Core.request("POST", '/api/stats-builder/sales', lastYearOptions)
          ])
          .then(([current, lastYear]) => {
            /* Set lastYear data to overlap the current data */
            for (let line of lastYear.data) {
              if (line.day) {
                line.day = (parseInt(line.day.substr(0, 4)) + 1).toString() + line.day.substr(4)
              } else if (line.month) {
                line.month = (parseInt(line.month.substr(0, 4)) + 1).toString() + line.month.substr(4)
              }
            }

            /* Divide data by company */
            this.currentByCompany = this.filledAndSlicedByCompany(current.data, timeFrame, start, end);
            this.lastYearByCompany = this.filledAndSlicedByCompany(lastYear.data, timeFrame, start, end);

            /* Find all different companies */
            let companies = {};
            for (let c of current.data) {
              if (!companies[c.cId]) {
                companies[c.cId] = {
                  id: c.cId,
                  name: c.companyName
                }
              }
            }
            for (let c of lastYear.data) {
              if (!companies[c.cId]) {
                companies[c.cId] = {
                  id: c.cId,
                  name: c.companyName
                }
              }
            }
            this.companies = companies;

            /* Show all companies by default */
            if (this.selectedCompanies.length === 0) {
              this.selectedCompanies = Object.keys(companies);
            }
          })
      },

      filledAndSlicedByCompany(data, timeFrame, start, end) {
        let tmp = {};
        for (let d of data) {
          if (!tmp[d.cId]) {
            tmp[d.cId] = []
          }
          tmp[d.cId].push(d)
        }
        let startMoment = start ? moment(start, this.dateTimeFormat) : null;
        let endMoment = end ? moment(end, this.dateTimeFormat) : null;
        for (let key of Object.keys(tmp)) {
          tmp[key] = this.fillMissing(tmp[key], 'subTotal', timeFrame, startMoment, endMoment);
        }
        return tmp
      },

      fillMissing(data, fieldName, period, start, end) {
        if (period === 'day') {
          return this.fillDates(data, {
            timeField: 'day',
            timeFormat: 'YYYYMMDD',
            field: fieldName,
            start: start,
            end: end,
            jump: { add: 1, period: 'day' }
          })
        } else if (period === 'month') {
          return this.fillDates(data, {
            timeField: 'month',
            timeFormat: 'YYYYMM',
            field: fieldName,
            start: start,
            end: end,
            jump: { add: 1, period: 'month' }
          })
        }
      },

      fillDates(data, options) {
        let timeField = options.timeField;
        let timeFormat = options.timeFormat;
        let field = options.field;
        let start = options.start;
        let end = options.end;
        let jump = options.jump;

        let existingData = data.reduce((obj, e) => {
          obj[e[timeField]] = e[field];
          return obj
        }, {});

        let fromDate = start ? start.clone() : moment(this.lowestKey(existingData), timeFormat);
        let toDate = end ? end.clone() : moment(this.highestKey(existingData), timeFormat);
        let iterDate = fromDate.clone();

        let tmp = [];
        while (toDate.diff(iterDate) >= 0) {
          let key = iterDate.format(timeFormat);
          let keyFormatted = timeField === 'month' ? `${key.substring(4, 6)}/${key.substring(2, 4)}` : undefined;
          let value = existingData[key] || "0.00";
          tmp.push([keyFormatted || key, value]);
          iterDate.add(jump.add, jump.period)
        }
        return tmp;
      },

      filterOutPreviousLegend(legendItem) {
        return this.showLastYear
          ? legendItem.datasetIndex % 2 === 0
          : true
      },

      getCompanyName(cId) {
        for (let c of Object.values(this.companies)) {
          if (c.id === cId) {
            return c.name
          }
        }
        return cId;
      },

      updateGraph() {
        let data = [];
        let colors = [];
        for (let i = 0; i < this.selectedCompanies.length; i++) {
          let cId = this.selectedCompanies[i];
          let colorHex = this.colors[i];

          /* push a line for current */
          data.push({
            name: this.getCompanyName(cId),
            data: this.currentByCompany[cId]
          });
          colors.push(colorHex);

          /* push a line for lastYear */
          if (this.showLastYear) {
            data.push({
              name: this.getCompanyName(cId),
              data: this.lastYearByCompany[cId],
              dataset: {
                borderDash: [10]
              }
            });
            colors.push(colorHex);
          }
        }
        this.lineChart.data = data;
        this.lineChart.colors = colors;
      }
    },

    watch: {
      selectedCompanies() {
        this.updateGraph()
      },
      showLastYear() {
        this.updateGraph()
      },
      currentByCompany() {
        this.updateGraph()
      }
    }
  }
</script>

<style lang="scss" scoped>
  .mr-2 {
    margin-right: 10px;
  }

  hr {
    border: 1px dashed #d9d9d9;
    margin: 20px 0px;
  }

  .middle {
    display: flex;
  }

  .chart {
    background-color: white;
    padding: 20px;
    border-radius: 4px;
    flex-grow: 1;
    flex-basis: 0;
    overflow-x: hidden;

    &:first-of-type {
      margin-right: 20px;
    }

    .title {
      font-size: 20px;
      display: flex;
      margin-bottom: 20px;
      font-weight: bold;

      .separator {
        margin: 0 10px;
        font-weight: normal;
        color: #e6e6e6;
      }

      .fade {
        color: #e6e6e6;
        font-weight: normal;
      }
    }
  }
</style>